<template>
  <div class="wizard-step-1">
    <div class="info-card">
      <div class="info-card-icon">
        <i class="pi pi-info-circle"></i>
      </div>
      <div class="info-card-content">
        <span class="info-card-title">{{ t('Informações da Franquia') }}</span>
        <span class="info-card-text">{{ t('Dados cadastrais da franquia') }}</span>
      </div>
    </div>

    <div class="info-section">
      <h4 class="section-title">
        <i class="pi pi-id-card"></i>
        {{ t('Dados Cadastrais') }}
      </h4>

      <div class="info-grid">
        <div class="info-card-item">
          <div class="info-card-item-icon">
            <i class="pi pi-building"></i>
          </div>
          <div class="info-card-item-content">
            <div class="field-group">
              <label class="field-label">
                {{ t('Nome da Franquia') }}
              </label>
              <input-text
                data-testid="franquias-wizard-step1-nome"
                v-model="localForm.nome"
                :placeholder="t('Nome da franquia')"
                elem-id="franquia-nome"
              />
            </div>
          </div>
        </div>

        <div class="info-card-item">
          <div class="info-card-item-icon">
            <i class="pi pi-envelope"></i>
          </div>
          <div class="info-card-item-content">
            <div class="field-group">
              <label class="field-label">
                {{ t('Email') }}
              </label>
              <input-text
                data-testid="franquias-wizard-step1-email"
                v-model="localForm.email"
                :placeholder="t('Email da franquia')"
                elem-id="franquia-email"
              />
            </div>
          </div>
        </div>

        <div class="info-card-item">
          <div class="info-card-item-icon">
            <i class="pi pi-file-text"></i>
          </div>
          <div class="info-card-item-content">
            <div class="field-group">
              <label class="field-label">
                {{ props.documentLabel || t('Documento') }}
              </label>
              <input-text
                data-testid="franquias-wizard-step1-cnpj"
                v-model="localForm.cnpj"
                :placeholder="t('Documento da franquia')"
                elem-id="franquia-cnpj"
              />
            </div>
          </div>
        </div>

        <div class="info-card-item">
          <div class="info-card-item-icon">
            <i class="pi pi-globe"></i>
          </div>
          <div class="info-card-item-content">
            <div class="field-group">
              <label class="field-label">
                {{ t('País') }}
              </label>
              <select-list
                data-testid="franquias-wizard-step1-pais"
                v-model="localForm.pais_id"
                :placeholder="t('Escolha')"
                :itens-select="availablePaises"
                elem-id="franquia-pais"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface PaisItem {
  ID?: Id;
  Name?: string;
  Code?: string;
  [key: string]: unknown;
}

interface FranquiaInner {
  Nome?: string;
  Email?: string;
  CNPJ?: string;
  PaisId?: Id;
  [key: string]: unknown;
}

interface FranquiaData {
  Franquia?: FranquiaInner;
  Pais?: string;
  [key: string]: unknown;
}

interface FormDataInput {
  nome?: string;
  email?: string;
  cnpj?: string;
  pais_id?: Id | null;
  pais_nome?: string;
  pais_code?: string;
  [key: string]: unknown;
}

interface LocalForm {
  nome: string;
  email: string;
  cnpj: string;
  pais_id: Id | null;
}

interface Props {
  franquiaId?: number | null;
  franquiaData?: FranquiaData | null;
  formData?: FormDataInput;
  documento?: string;
  documentLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  franquiaData: null,
  formData: () => ({}),
  documento: '',
  documentLabel: 'Documento',
});

const emit = defineEmits<{
  'validation-change': [isValid: boolean];
  'form-update': [data: FormDataInput];
}>();

const { t } = useI18n();
const { fetchPaises } = useLocalidades();

const localForm = ref<LocalForm>({
  nome: '',
  email: '',
  cnpj: '',
  pais_id: null,
});

const availablePaises = ref<PaisItem[]>([]);

const syncLocalForm = (): void => {
  const franquia = props.franquiaData?.Franquia || {};

  localForm.value.nome = props.formData?.nome || franquia.Nome || '';
  localForm.value.email = props.formData?.email || franquia.Email || '';
  localForm.value.cnpj = props.formData?.cnpj || props.documento || franquia.CNPJ || '';
  localForm.value.pais_id = props.formData?.pais_id || franquia.PaisId || null;
};

const syncFromFormData = (newFormData: FormDataInput | undefined): void => {
  if (!newFormData) {
    return;
  }

  if (!localForm.value.nome && newFormData.nome) {
    localForm.value.nome = newFormData.nome;
  }

  if (!localForm.value.email && newFormData.email) {
    localForm.value.email = newFormData.email;
  }

  if (!localForm.value.cnpj && newFormData.cnpj) {
    localForm.value.cnpj = newFormData.cnpj;
  }

  if (newFormData.pais_id && localForm.value.pais_id !== newFormData.pais_id) {
    localForm.value.pais_id = newFormData.pais_id;
  }
};

const emitFormUpdate = (): void => {
  const paisItem = availablePaises.value.find((pais) => String(pais.ID) === String(localForm.value.pais_id));

  emit('form-update', {
    nome: localForm.value.nome,
    email: localForm.value.email,
    cnpj: localForm.value.cnpj,
    pais_id: localForm.value.pais_id,
    pais_nome: paisItem?.Name || props.formData?.pais_nome || props.franquiaData?.Pais || '',
    // ISO code resolved from the locations-service registry row the user
    // picked — this is what actually gets written to franquias.pais (S1b:
    // storage must be the code, pais_nome stays display-only).
    pais_code: paisItem?.Code || props.formData?.pais_code || '',
  });
};

const emitValidation = (): void => {
  const isValid = (localForm.value.nome || '').trim().length >= 2;
  emit('validation-change', isValid);
};

watch(() => props.formData, (newFormData) => {
  syncFromFormData(newFormData);
}, { deep: true, immediate: true });

watch(() => props.franquiaData, () => {
  syncLocalForm();
}, { deep: true });

watch(() => props.documento, (newDocumento) => {
  if (!props.formData?.cnpj && newDocumento && localForm.value.cnpj !== newDocumento) {
    localForm.value.cnpj = newDocumento;
  }
});

watch(localForm, () => {
  emitFormUpdate();
  emitValidation();
}, { deep: true });

onMounted(async () => {
  syncLocalForm();

  try {
    availablePaises.value = (await fetchPaises()) as PaisItem[];
  } catch (err) {
    log('FranquiasWizardStep1', 'onMounted', 'Error loading countries', err);
  }

  if (localForm.value.pais_id) {
    const savedPaisId = localForm.value.pais_id;
    localForm.value.pais_id = null;
    await nextTick();
    localForm.value.pais_id = savedPaisId;
  }

  emitFormUpdate();
  emitValidation();
});
</script>

<style scoped>
.wizard-step-1 {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    var(--tenant-secondary-color),
    var(--tenant-secondary-hover-color, var(--tenant-secondary-color))
  );
  border: 1px solid var(--tenant-secondary-hover-color, var(--tenant-secondary-color));
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-card-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-card-icon i {
  font-size: 20px;
  color: #ffffff;
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
}

.info-card-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.info-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 20px;
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  color: var(--tenant-secondary-color);
  font-size: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-card-item {
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 14px 16px;
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.info-card-item:hover {
  border-color: var(--tenant-secondary-color);
  background: rgba(var(--app-secondary-color-rgb), 0.06);
}

.info-card-item-icon {
  width: 36px;
  height: 36px;
  background: var(--surface-100);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-card-item-icon i {
  font-size: 16px;
  color: var(--tenant-secondary-color);
}

.info-card-item-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.field-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 640px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .info-card {
    flex-direction: column;
    text-align: center;
  }
}
</style>
